import { Sparkles } from 'lucide-react-native';
import { Platform, StyleSheet, View } from 'react-native';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { FadeIn } from '../animations';
import { Text } from '../primitives/Text';

interface LandingSectionHeadingProps {
  /** Small uppercase pill above the title (e.g. "THE PRODUCT"). */
  eyebrow?: string;
  /** Plain statement line, rendered in the Sora display family. */
  title: string;
  /** Italic accent line shown beneath the title — the editorial "moment". */
  accent?: string;
  /** Supporting copy beneath the headline. */
  subtitle?: string;
  align?: 'center' | 'left';
  /** Show the leading sparkle icon in the eyebrow pill. */
  showEyebrowIcon?: boolean;
  /** 'hero' bumps the headline scale for above-the-fold use. */
  size?: 'section' | 'hero';
}

export const LandingSectionHeading = ({
  eyebrow,
  title,
  accent,
  subtitle,
  align = 'center',
  showEyebrowIcon = true,
  size = 'section',
}: LandingSectionHeadingProps) => {
  const isCenter = align === 'center';
  const textAlign = isCenter ? 'center' : 'left';
  const isHero = size === 'hero';

  return (
    <View style={[styles.container, isCenter ? styles.center : styles.left]}>
      {eyebrow ? (
        <FadeIn delay={0} translateY={12}>
          <View style={styles.eyebrow}>
            {showEyebrowIcon ? (
              <Sparkles size={13} color={colors.primary[400]} strokeWidth={2} />
            ) : null}
            <Text variant="overline" color={colors.primary[400]} style={styles.eyebrowText}>
              {eyebrow}
            </Text>
          </View>
        </FadeIn>
      ) : null}

      <FadeIn delay={80} translateY={16}>
        <Text
          variant="h2"
          aria-level={2}
          align={textAlign}
          style={isHero ? styles.titleHero : styles.title}
        >
          {title}
        </Text>
        {accent ? (
          <Text
            variant="h2"
            accessibilityRole="none"
            italic
            align={textAlign}
            color={colors.primary[400]}
            style={isHero ? styles.accentHero : styles.accent}
          >
            {accent}
          </Text>
        ) : null}
      </FadeIn>

      {subtitle ? (
        <FadeIn delay={160} translateY={16}>
          <Text
            variant="bodyLarge"
            color={colors.text.secondary}
            align={textAlign}
            style={isHero ? styles.subtitleHero : styles.subtitle}
          >
            {subtitle}
          </Text>
        </FadeIn>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: spacing.md,
  },
  center: {
    alignItems: 'center',
  },
  left: {
    alignItems: 'flex-start',
  },
  eyebrow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.primary.muted,
    borderWidth: 1,
    borderColor: colors.primary.subtle,
  },
  eyebrowText: {
    color: colors.primary[400],
  },
  title: {
    fontSize: Platform.select({ web: 44, default: 32 }),
    lineHeight: Platform.select({ web: 50, default: 40 }),
  },
  accent: {
    fontSize: Platform.select({ web: 44, default: 32 }),
    lineHeight: Platform.select({ web: 52, default: 40 }),
  },
  titleHero: {
    fontSize: Platform.select({ web: 64, default: 38 }),
    lineHeight: Platform.select({ web: 70, default: 46 }),
  },
  accentHero: {
    fontSize: Platform.select({ web: 64, default: 38 }),
    lineHeight: Platform.select({ web: 74, default: 46 }),
  },
  subtitle: {
    maxWidth: 620,
    marginTop: spacing.xs,
  },
  subtitleHero: {
    maxWidth: 600,
    marginTop: spacing.sm,
    fontSize: Platform.select({ web: 20, default: 16 }),
  },
});
